import { Header, Footer } from '@/shared/components/layout';
import { useDocumentTitle } from '@/shared/hooks';
import { Hero24, Features7, ContentSection01 } from './Components';
import { ContentSection02, Statistic1 } from '@/features/HowItWorks/Components';
import { TryItFree } from '@/features/Pricing/Components/TryItFree';

const BrokeragesPage = () => {
  useDocumentTitle('Real Estate Leads for Brokerages Canada - BrokerMax');
  return (
    <div className="page font--jakarta">
      <Header variant="light" />
      <main>
        <Hero24
          title="Grow your brokerage by providing your agents with leads that close"
          description="Empower your team with qualified leads that convert. Give your agents the tools they need to succeed and grow your brokerage."
          backgroundImage="/images/hero-24.jpg"
        />
        <div className="container mx-auto max-w-7xl py-12 px-4">
          <div className="flex flex-wrap gap-8 opacity-30 mb-5 justify-center">
            {[
              {
                src: '/images/realtor-canada.png',
                alt: 'REALTOR.ca'
              },
              { src: '/images/brokerbay.png', alt: 'BrokerBay' },
              { src: '/images/canada.jpg', alt: 'Canada' },
              {
                src: '/images/proptx.png',
                alt: 'PropTx',
                padding: '10px'
              },
              {
                src: '/images/crea.png',
                alt: 'CREA',
                padding: '0px'
              }
            ].map((logo, i) => (
              <img
                key={i}
                src={logo.src}
                alt={logo.alt}
                className="h-12 object-contain"
                style={{ padding: logo.padding }}
              />
            ))}
          </div>
          {/* END BADGES */}
        </div>
        <ContentSection01
          sectionId="lnk-1"
          sectionIdText="Brokerage Growth"
          title="Scale your brokerage with proven lead generation"
          description="Provide your agents with a steady stream of qualified leads that convert. Our platform helps brokerages retain top talent and attract new agents by offering industry-leading lead generation tools."
          subtitle="Results that matter"
          items={[
            'Increase agent retention by providing valuable lead generation resources',
            'Attract top-performing agents with competitive lead generation capabilities',
            'Scale your brokerage revenue through improved agent productivity and conversions'
          ]}
          image="/images/img-05.png"
          imageAlt="Brokerage dashboard"
          imageFirst={false}
        />
        <Features7
          title="Enterprise Solutions for Brokerages"
          description="Everything you need to support your agents and grow your brokerage"
          features={[
            {
              title: 'Multi-Agent Support',
              description:
                'Manage leads and track performance across your entire team',
              icon: 'flaticon-layers-1'
            },
            {
              title: 'Centralized Management',
              description:
                'Monitor agent activity and lead distribution from one dashboard',
              icon: 'flaticon-click-1'
            },
            {
              title: 'Customizable Distribution',
              description:
                'Configure lead routing rules that match your brokerage structure',
              icon: 'flaticon-prioritize'
            },
            {
              title: 'Performance Analytics',
              description:
                'Track ROI and conversion rates across your brokerage',
              icon: 'flaticon-analytics'
            }
          ]}
        />
        <ContentSection02
          image="/images/img-07.png"
          imageAlt="Brokerage management dashboard"
          textBoxes={[
            {
              title: 'Agent Retention & Recruitment',
              description:
                'Top agents stay with brokerages that provide value. BrokerMax gives you a competitive advantage by offering your agents access to high-quality leads that close, making your brokerage more attractive to both current and prospective agents.'
            },
            {
              title: 'Scalable Lead Generation',
              description:
                'As your brokerage grows, your lead generation needs grow too. Our platform scales with you, providing consistent lead quality whether you have 10 agents or 100. No matter how large your team becomes, every agent gets access to qualified leads.'
            }
          ]}
        />
        <Statistic1
          statistics={[
            {
              value: '300',
              suffix: '+',
              label: 'Brokerages trust BrokerMax for lead generation'
            },
            {
              value: '85',
              suffix: '%',
              label: 'Agent retention rate improvement'
            },
            {
              value: '2.5',
              suffix: 'x',
              label: 'Average revenue increase per agent'
            }
          ]}
        />
        <ContentSection02
          image="/images/img-02.png"
          imageAlt="Brokerage lead management system"
          textBoxes={[
            {
              title: 'Easy Onboarding',
              description:
                'Get your entire team set up quickly. Our platform integrates seamlessly with your existing systems and workflows, so agents can start receiving leads immediately without disrupting their current processes.'
            },
            {
              title: 'Automated Lead Distribution',
              description:
                'Set up rules for automatic lead distribution based on territory, agent specialization, or performance metrics. Leads are delivered directly to agents, ensuring fast response times and optimal conversion rates.'
            }
          ]}
        />
        <ContentSection01
          sectionId="lnk-3"
          sectionIdText="Complete Platform"
          title="Everything your brokerage needs in one platform"
          description="From lead generation to conversion tracking, BrokerMax provides all the tools your brokerage needs to support agent success and drive growth. No need for multiple platforms or complex integrations."
          image="/images/img-05.png"
          imageAlt="All-in-one brokerage platform"
          imageFirst={true}
        />
        <TryItFree />
      </main>
      <Footer />
    </div>
  );
};

export default BrokeragesPage;
